/**
 * PreScreenTrigger — Send Pre-Screen Link
 *
 * Replaces the old scheduling UI. Sends a magic-link pre-screen invitation
 * to the candidate via email. The candidate completes the pre-screen in
 * their browser at their own convenience — no scheduling required.
 *
 * Decision (2026-04-06): No Calendly / TidyCal. No live voice scheduling.
 * The magic link IS the pre-screen trigger.
 */

import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Send, Link2, CheckCircle2, Loader2, Sparkles } from "lucide-react";
import { useState, useEffect } from "react";
import { engineApiClient } from "@/lib/apiClient";
import { supabase } from "@/lib/supabase";
import type { CandidateScreeningResult } from "@/lib/contracts";

const API_BASE = (import.meta.env.VITE_ENGINE_API_BASE_URL ?? '').replace(/\/$/, '');

interface SendLinkResult {
  token: string;
  link: string;
  emailed: boolean;
  skipped: boolean;
}

export function PreScreenTrigger() {
  const { id, candidateId } = useParams<{ id: string; candidateId: string }>();
  const navigate = useNavigate();

  const projectId = id ?? "";
  const candId = candidateId ?? "";

  const [candidate, setCandidate] = useState<CandidateScreeningResult | null>(null);
  const [loadingCandidate, setLoadingCandidate] = useState(true);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<SendLinkResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    engineApiClient.getCandidateScreening(projectId, candId)
      .then(setCandidate)
      .catch(() => setCandidate(null))
      .finally(() => setLoadingCandidate(false));
  }, [projectId, candId]);

  const handleSendLink = async () => {
    setSending(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;

      const res = await fetch(
        `${API_BASE}/api/engine/projects/${projectId}/candidates/${candId}/pre-screen/send-link`,
        { method: 'POST', headers },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(body.error ?? `Failed (${res.status})`);
      }
      const data = await res.json() as SendLinkResult;
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send link. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#02182B]">
      <header className="border-b border-[#233D4C] bg-[#02182B] sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Link to={`/project/${id}/candidate/${candidateId}`} className="text-gray-400 hover:text-white transition-colors">
            <ArrowLeft size={20} />
          </Link>
          <div>
            <h1 className="text-white text-xl font-semibold">Send Pre-Screen</h1>
            <p className="text-gray-400 text-sm">Send a magic-link pre-screen invitation to the candidate</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {loadingCandidate ? (
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <Loader2 size={16} className="animate-spin" /> Loading candidate…
          </div>
        ) : candidate ? (
          <div className="bg-[#233D4C] rounded-lg border border-[#2a4a5c] p-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-[#FD802E]/10 flex items-center justify-center text-[#FD802E] font-semibold text-lg">
              {candidate.candidateName.split(' ').map((n) => n[0]).join('').slice(0, 2)}
            </div>
            <div>
              <div className="text-white font-semibold">{candidate.candidateName}</div>
              <div className="text-gray-400 text-sm">{candidate.candidateTitle}</div>
              <div className="text-[#FD802E] text-xs mt-1">{candidate.fitScore}% match</div>
            </div>
          </div>
        ) : null}

        <div className="bg-[#233D4C] rounded-lg border border-[#2a4a5c] p-6">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Sparkles className="text-[#FD802E]" size={18} />How it works
          </h3>
          <div className="space-y-3">
            {[
              "Erica emails the candidate a unique pre-screen link",
              "The candidate clicks and completes a short conversation in their browser",
              "They can come back later using the same link if needed",
              "Once done, Erica scores their answers and updates their profile",
            ].map((text, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-[#FD802E]/10 flex items-center justify-center text-[#FD802E] text-xs font-bold flex-shrink-0 mt-0.5">
                  {i + 1}
                </div>
                <p className="text-gray-300 text-sm">{text}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-[#02182B] border border-[#FD802E]/20 rounded-lg p-4">
          <p className="text-xs text-[#FD802E] font-semibold uppercase tracking-wide mb-2">Erica's Opening Statement</p>
          <p className="text-gray-400 text-xs italic leading-relaxed">
            "Hi, I'm Erica — an AI assistant supporting the hiring process. I'll be conducting a short pre-screen conversation on behalf of the search team. Everything we discuss will be shared with the recruiting team. Shall we get started?"
          </p>
          <p className="text-gray-600 text-xs mt-2">This disclosure is always delivered first. It is hardcoded and cannot be removed.</p>
        </div>

        {result ? (
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-6 space-y-3">
            <div className="flex items-center gap-2 text-green-400 font-semibold">
              <CheckCircle2 size={20} />
              {result.emailed ? "Pre-screen link sent!" : "Link generated (no email on file — share manually)"}
            </div>
            {result.skipped && (
              <p className="text-yellow-400 text-sm">⚠ No email address on file — share the link below with the candidate directly.</p>
            )}
            <div className="bg-[#02182B] rounded-lg p-3 flex items-center gap-2">
              <Link2 size={14} className="text-gray-400 flex-shrink-0" />
              <code className="text-[#FD802E] text-xs break-all">{result.link}</code>
            </div>
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => navigator.clipboard.writeText(result.link)}
                className="px-4 py-2 bg-[#233D4C] text-white rounded-lg text-sm hover:bg-[#2a4a5c] transition-colors"
              >
                Copy link
              </button>
              <button
                onClick={() => navigate(`/project/${id}/candidate/${candidateId}`)}
                className="px-4 py-2 bg-[#FD802E] text-[#233D4C] rounded-lg text-sm font-semibold hover:bg-[#ff8f45] transition-colors"
              >
                Back to candidate
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <Link
              to={`/project/${id}/candidate/${candidateId}`}
              className="px-6 py-3 bg-[#233D4C] text-white rounded-lg hover:bg-[#2a4a5c] transition-colors font-medium text-sm"
            >
              Cancel
            </Link>
            <button
              onClick={handleSendLink}
              disabled={sending}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-[#FD802E] text-[#233D4C] rounded-lg hover:bg-[#ff8f45] transition-colors font-semibold disabled:opacity-70 text-sm"
            >
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              {sending ? "Sending…" : "Send Pre-Screen Link"}
            </button>
          </div>
        )}

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-400 text-sm">
            {error}
          </div>
        )}
      </main>
    </div>
  );
}
